import { useLocalStorage } from "@vueuse/core";
import { inject, computed } from "vue";
import { configInjectionKey } from "@/core/injection-keys";
import { truncate } from "@/core/utilities";
import { useNotifications } from "@/shared/notification";
import type { Product } from "@/core/api/graphql/types";
import { DEFAULT_NOTIFICATION_DURATION } from "@/core/constants";
import { useI18n } from "vue-i18n";

const NOTIFICATIONS_GROUP = "compare-pruducts";
const DEFAULT_MAX_PRODUCTS = 5;
const NAME_MAX_LENGTH = 60;

const productsIds = useLocalStorage<string[]>("productCompareListIds", []);

export function useCompareProducts() {
  const { t } = useI18n();

  const config = inject(configInjectionKey);
  const notifications = useNotifications();
  const productsLimit = config?.product_compare_limit || DEFAULT_MAX_PRODUCTS;

  function addToCompareList(product: Product) {
    if (productsIds.value.includes(product.id)) {
      return;
    }

    if (productsIds.value.length >= productsLimit) {
      notifications.warning({
        duration: DEFAULT_NOTIFICATION_DURATION,
        group: NOTIFICATIONS_GROUP,
        singleInGroup: true,
        text: t("custom.compare.notifications.limit", productsLimit),
      });

      return;
    }

    productsIds.value.push(product.id);

    const productName = truncate(product.name, NAME_MAX_LENGTH);
    const itemsLeft = productsLimit - productsIds.value.length;

    const notificationMessage = t("custom.compare.notifications.product_added_to_compare_list", {
      productName,
      itemsLeft,
    });

    notifications.success({
      duration: DEFAULT_NOTIFICATION_DURATION,
      group: NOTIFICATIONS_GROUP,
      singleInGroup: true,
      html: notificationMessage,
      button: {
        text: "Compare",
        to: { path: "/compare" },
        clickHandler() {
          notifications.clear(NOTIFICATIONS_GROUP);
        },
      },
    });
  }

  function removeFromCompareList(product: Product) {
    const index = productsIds.value.indexOf(product.id);

    if (index === -1) {
      return;
    }

    productsIds.value.splice(index, 1);

    const productName = truncate(product.name, NAME_MAX_LENGTH);

    const notificationMessage = t("custom.compare.notifications.product_removed_from_compare_list", {
      productName,
    });

    notifications.warning({
      duration: DEFAULT_NOTIFICATION_DURATION,
      group: NOTIFICATIONS_GROUP,
      singleInGroup: true,
      html: notificationMessage,
    });
  }

  function clearCompareList() {
    productsIds.value = [];
  }

  return {
    addToCompareList,
    removeFromCompareList,
    clearCompareList,
    productsLimit,
    productsIds: computed(() => productsIds.value.slice(0, productsLimit)),
  };
}
